// The script and its knowledge check, as the approver reads them (E31-S6-T3; mockups/script-review.html,
// "Script and stills" and "Knowledge check"): each scene's still with its highlight box, its heading,
// the guide page it cites, and the narration exactly as the presenter will say it; then each question
// with its options, the right one marked, and why. Every word here is the version's own, from the API.
import type { Scene } from "@waypoint/contracts";
import { cn } from "@waypoint/surface";
import { useState } from "react";
import { t } from "../../i18n/t.js";
import { StatusChip } from "../../ui/form.js";
import { secondsFor, stillPath, wordsOf, type VersionDetail } from "../../versions/model.js";
import { readQuestions } from "../../versions/questions.js";

const SCENE_CARD = "rounded-12 border border-wp-line bg-wp-panel/74";
const MONO = "font-mono text-12 font-semibold text-wp-text-3";

/** The guide page a scene or question cites (.cite). */
export function Cite({ pages }: { pages: string }) {
  return (
    <span
      aria-label={t("review.cite", { pages })}
      className="rounded-pill border border-wp-accent/44 bg-wp-accent/13 px-1.75 py-0.5 font-mono text-11 font-semibold whitespace-nowrap text-wp-accent"
    >
      {pages}
    </span>
  );
}

function headingOf(scene: Scene): string {
  if (scene.kind === "title") return scene.text;
  if (scene.kind === "still" && scene.kicker !== undefined) return scene.kicker;
  return t(`review.scene.${scene.kind}`);
}

interface Size {
  readonly w: number;
  readonly h: number;
}

/** A scene's still, from the version's still route, with its highlight box drawn over it. */
function Still({ versionId, scene, n }: { versionId: string; scene: Scene; n: number }) {
  const [size, setSize] = useState<Size | undefined>(undefined);
  const frame =
    "relative grid aspect-video w-full max-w-180 place-items-center overflow-hidden rounded-8 border border-wp-line-strong bg-linear-to-b from-wp-panel-2 to-wp-bg/58 text-12 text-wp-text-3";
  if (scene.kind !== "still" && scene.kind !== "video")
    return (
      <div className={cn(frame, "p-2 text-center")}>
        <span>{t("review.still.none", { what: headingOf(scene) })}</span>
      </div>
    );
  const box = scene.kind === "still" ? scene.box : undefined;
  const name = scene.kind === "still" ? scene.kicker : undefined;
  return (
    <div className={frame}>
      <img
        src={stillPath(versionId, scene.stillId)}
        alt={
          name === undefined
            ? t("review.still.alt", { n })
            : t("review.still.altNamed", { n, name })
        }
        className="absolute inset-0 size-full object-contain"
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0)
            setSize({ w: img.naturalWidth, h: img.naturalHeight });
        }}
      />
      {box !== undefined && size !== undefined ? (
        <div
          aria-hidden
          data-box
          className="absolute rounded-4 border-2 border-wp-accent shadow-wp-glow"
          style={{
            left: `${String((box.x / size.w) * 100)}%`,
            top: `${String((box.y / size.h) * 100)}%`,
            width: `${String((box.w / size.w) * 100)}%`,
            height: `${String((box.h / size.h) * 100)}%`,
          }}
        />
      ) : null}
    </div>
  );
}

export function Scenes({ version }: { version: VersionDetail }) {
  const id = version.id ?? "";
  return (
    <ol className="m-0 grid list-none gap-3 p-0">
      {version.script.scenes.map((scene, i) => {
        const n = i + 1;
        const words = wordsOf(scene.narration);
        return (
          <li key={n} className={cn(SCENE_CARD, "grid gap-3.5 p-3")}>
            <Still versionId={id} scene={scene} n={n} />
            <div className="min-w-0">
              <div className="mb-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                <span className={MONO}>{t("review.scene", { n })}</span>
                <h3 className="flex-auto text-14 font-semibold">{headingOf(scene)}</h3>
                <Cite pages={scene.citation.pages} />
              </div>
              <p className="m-0 text-15 leading-body">{scene.narration}</p>
              <div className="mt-2 text-12 text-wp-text-3">
                {t("review.scene.words", { words, seconds: secondsFor(words) })}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** How many questions the set holds, or undefined where it cannot be laid out. */
export function questionCount(markdown: string): number | undefined {
  return readQuestions(markdown)?.reduce((n, s) => n + s.questions.length, 0);
}

export function Questions({ markdown }: { markdown: string }) {
  const segments = readQuestions(markdown);
  if (segments === undefined)
    return (
      <div className="grid gap-2.5">
        <p className="m-0 text-13 text-wp-text-2">{t("review.qs.unread")}</p>
        <pre className="m-0 overflow-x-auto rounded-8 border border-wp-line bg-wp-bg/58 p-3 font-mono text-12 whitespace-pre-wrap">
          {markdown}
        </pre>
      </div>
    );
  let q = 0;
  return (
    <div className="grid gap-3">
      <p className="m-0 text-13 text-wp-text-2">{t("review.qs.note")}</p>
      {segments.map((segment) => (
        <section key={segment.n} className="grid gap-3">
          {segments.length > 1 ? (
            <h3 className="m-0 text-13 font-semibold text-wp-text-2">
              {t("review.qs.segment", { n: segment.n, label: segment.label })}
            </h3>
          ) : null}
          <ol className="m-0 grid list-none gap-3 p-0">
            {segment.questions.map((question) => {
              q += 1;
              return (
                <li key={q} className={cn(SCENE_CARD, "grid gap-2 px-3.5 py-3")}>
                  <h4 className="m-0 flex flex-wrap items-baseline gap-2.5 text-15 font-semibold">
                    <span className={MONO}>{t("review.qs.q", { n: q })}</span>
                    <span className="min-w-0 flex-1">{question.prompt}</span>
                    {question.pages === undefined ? null : <Cite pages={question.pages} />}
                  </h4>
                  <ul className="m-0 grid list-none gap-1 p-0">
                    {question.options.map((option) => (
                      <li
                        key={option.id}
                        data-right={option.right}
                        className={cn(
                          "flex items-baseline gap-2 rounded-8 border px-2 py-1.25 text-14",
                          option.right
                            ? "border-wp-accent/44 bg-wp-accent/13"
                            : "border-wp-line bg-wp-bg/58",
                        )}
                      >
                        <b className={MONO}>{option.id.toUpperCase()}</b>
                        <span className="min-w-0 flex-1">{option.text}</span>
                        {option.right ? (
                          <StatusChip tone="done">{t("review.qs.right")}</StatusChip>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  <div className="text-13 text-wp-text-2">
                    {t("review.qs.why", { why: question.why })}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
